import { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useLadderPrices, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { PriceBadge } from "../site/PriceBadge";
import { TokenIcon } from "../fun-ui/icons";
import { BadgeType } from "@/stores/slices/commonSlice";
import { PaymentBenefitsCompareTable } from "./PaymentBenefitsCompareTable";
import { ladderUserTierFromVip, isPaidVip, matchLadderUserTier, PAYMENT_DIALOG_NOTICE_HEIGHT_PX, PAYMENT_OPTION_CARD_MIN_HEIGHT_PX } from "@/lib/payment";
import Loading from "../Loading";

type PaymentDialogRechargeProps = {
  subtitle?: string | React.JSX.Element;
  scale: number;
  isActive: boolean;
  activedIndex?: number;
  onActivedIndexChange?: (index: number) => void;
  onPaymentOptionChange: (option: { renewalDays: number, paymentPrice: number }) => void;
  leftColumnFooter?: ReactNode;
}
export function PaymentDialogRecharge({
  subtitle: propSubtitle,
  scale = 1,
  isActive,
  activedIndex: propActivedIndex,
  onActivedIndexChange,
  onPaymentOptionChange,
  leftColumnFooter,
}: PaymentDialogRechargeProps) {
  const [localActivedIndex, setLocalActivedIndex] = useState<number>();

  const activedIndex = propActivedIndex !== undefined ? propActivedIndex : localActivedIndex;

  const setActivedIndex = useCallback((index: number) => {
    if (onActivedIndexChange) {
      onActivedIndexChange(index);
    } else {
      setLocalActivedIndex(index);
    }
  }, [onActivedIndexChange]);

  const {
    paymentOpen,
    setPaymentPrice,
    getPaymentPrice,
  } = usePayment();

  const {
    vip: isVip,
    vipType,
  } = useUserVip();

  const {
    value,
    fetch,
    loading: ladderLoading,
  } = useLadderPrices().ladderPrices;

  useEffect(() => {
    if (!value || value.length === 0) {
      fetch();
    }
  }, [fetch, value]);

  const prices = useMemo(() => {
    if (!value) return [];

    const currentUserType = ladderUserTierFromVip(isVip, vipType);

    return value.filter(({ price, stage, users }) => {
      return price > 0 && stage.includes('pc') && matchLadderUserTier(users, currentUserType);
    });
  }, [value, isVip, vipType]);

  const showRechargePriceLoading = isActive && ladderLoading && prices.length === 0;

  const paymentPrice = getPaymentPrice('recharge');
  const paymentPriceRef = useRef(paymentPrice);
  const isUpdatingFromPriceRef = useRef(false);
  const hasCalledOnPaymentOptionChangeRef = useRef(false);
  const isUserClickingRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;
    if (isUserClickingRef.current) {
      isUserClickingRef.current = false;
      return;
    }
    if (paymentPrice !== 0 && prices.length > 0) {
      const index = prices.findIndex(({ price }) => price === paymentPrice);
      if (index !== -1) {
        if (index !== (activedIndex ?? localActivedIndex)) {
          isUpdatingFromPriceRef.current = true;
          hasCalledOnPaymentOptionChangeRef.current = false;
          setActivedIndex(index);
        } else if (!hasCalledOnPaymentOptionChangeRef.current) {
          hasCalledOnPaymentOptionChangeRef.current = true;
          onPaymentOptionChange({ renewalDays: 0, paymentPrice });
        }
      }
    }
  }, [isActive, paymentPrice, prices, setActivedIndex, activedIndex, localActivedIndex, onPaymentOptionChange]);

  useEffect(() => {
    paymentPriceRef.current = paymentPrice;
  }, [paymentPrice]);

  useEffect(() => {
    if (!isActive) return;
    if (typeof activedIndex === 'undefined') {
      return;
    }
    const price = prices[activedIndex]?.price;
    if (price && price !== 0) {
      if (isUpdatingFromPriceRef.current) {
        isUpdatingFromPriceRef.current = false;
        hasCalledOnPaymentOptionChangeRef.current = true;
        onPaymentOptionChange({ renewalDays: 0, paymentPrice: price });
        return;
      }

      if (paymentPriceRef.current !== price) {
        onPaymentOptionChange({ renewalDays: 0, paymentPrice: price });
        setPaymentPrice(price, 'recharge');
      }
    }
  }, [isActive, prices, activedIndex, setPaymentPrice, onPaymentOptionChange, setActivedIndex]);

  const {
    userQuotas,
    balanceInsufficient: payment,
  } = useUser();
  let activedPrice = 0;
  let subtitle = <>你的飞豆余额不足，立即充值继续享用 AI 能力，加速论文读写</>;
  if (typeof payment === 'number' && (userQuotas?.balance || userQuotas?.balance === 0)) {
    if (payment === 0) {
      if (userQuotas.balance === 0) {
        subtitle = <>你的飞豆余额不足，立即充值继续享用 AI 能力，加速论文读写</>;
      } else {
        subtitle = (
          <>
            当前剩{' '}
            <span className="font-semibold text-foreground">{userQuotas.balance}</span>
            {' '}飞豆，你可选择合适的飞豆充值数
          </>
        );
      }
    } else {
      activedPrice = payment - userQuotas.balance;
      subtitle = (
        <>
          当前剩{' '}
          <span className="font-semibold text-foreground">{userQuotas.balance}</span>
          {' '}飞豆，还需 {activedPrice} 飞豆，请选择合适的飞豆充值数
        </>
      );
    }
  }

  useEffect(() => {
    if (!isActive) return;
    if (paymentOpen === true && paymentPrice === 0) {
      setActivedIndex(0);
    }
  }, [isActive, paymentPrice, paymentOpen, setActivedIndex])

  const memberForPricing = isPaidVip(isVip);
  const rechargeFooterRef = useRef<HTMLDivElement | null>(null);
  const [rechargeFooterH, setRechargeFooterH] = useState(0);

  useEffect(() => {
    if (!leftColumnFooter) {
      setRechargeFooterH(0);
      return;
    }
    const el = rechargeFooterRef.current;
    if (!el) return;
    const sync = () => setRechargeFooterH(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [leftColumnFooter, scale, isActive, paymentPrice]);

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div
        className="mx-auto flex w-full shrink-0 items-center justify-center overflow-hidden text-center text-muted-foreground font-normal"
        style={{
          fontSize: `${16 * scale}px`,
          lineHeight: `${22 * scale}px`,
          height: `${PAYMENT_DIALOG_NOTICE_HEIGHT_PX * scale}px`,
          maxWidth: `${640 * scale}px`,
          paddingLeft: `${16 * scale}px`,
          paddingRight: `${16 * scale}px`,
          marginBottom: `${24 * scale}px`,
        }}
      >
        {subtitle}
      </div>
      <div className="relative min-h-0 w-full min-w-0 flex-1 overflow-hidden">
        <div
          className="absolute left-0 right-0 top-0 z-10 h-fit"
          style={{
            maxHeight: leftColumnFooter
              ? `calc(100% - ${Math.max(rechargeFooterH, Math.round(96 * scale))}px)`
              : '100%',
          }}
        >
          <div
            className="w-full shrink-0"
            style={{
              paddingLeft: `${32 * scale}px`,
              paddingRight: `${32 * scale}px`,
            }}
          >
            <div className="flex w-full min-w-0 flex-row flex-nowrap items-start" style={{ gap: `${20 * scale}px` }}>
              <div
                className="flex min-h-0 min-w-0 flex-col overflow-visible"
                style={{
                  flex: '466 1 0',
                  minWidth: 0,
                  gap: `${12 * scale}px`,
                }}
              >
                {showRechargePriceLoading ? (
                  <div
                    className="flex w-full items-center justify-center rounded-xl border border-[#E8E4E0] bg-background"
                    style={{ minHeight: `${PAYMENT_OPTION_CARD_MIN_HEIGHT_PX * scale * 2}px` }}
                  >
                    <Loading mask size="lg" />
                  </div>
                ) : (
                  <div className="flex flex-col" style={{ gap: `${16 * scale}px`, marginTop: `${2 * scale}px` }}>
                    {prices.map(({ price, vip, ...base }, index) => {
                      const { coins, label, desc, badge } = (memberForPricing ? vip : base);
                      const { coins: extraCoins, label: extraLabel } = (memberForPricing ? base : vip);
                      return (
                        <div
                          key={`${price}/${coins}`}
                          onClick={() => {
                            isUserClickingRef.current = true;
                            setActivedIndex(index);
                          }}
                          className={cn(
                            "flex flex-row w-full relative bg-[#F8FAFF] outline outline-background rounded-lg overflow-visible",
                          )}
                          style={{
                            borderRadius: `${6 * scale}px`,
                            outlineWidth: `${2 * scale}px`,
                            minHeight: `${100 * scale}px`,
                          }}
                        >
                          <div
                            className={cn(
                              "flex flex-1 items-center bg-background/90 backdrop-blur-md relative cursor-pointer",
                              "outline outline-offset-0 outline-transparent hover:outline-primary/50",
                              (activedIndex || 0) === index && "outline-primary",
                              "shadow-[8px_0px_30px_0_rgba(44,108,255,0.07)]",
                              "transition-all duration-500",
                            )}
                            style={{
                              borderRadius: `${6 * scale}px`,
                              padding: `${20 * scale}px ${24 * scale}px ${20 * scale}px ${16 * scale}px`,
                              outlineWidth: `${3 * scale}px`,
                              // 向内描边，避免首项 hover/selected 外描边被父容器裁切
                              outlineOffset: `${-3 * scale}px`,
                              minHeight: `${100 * scale}px`,
                            }}
                          >
                            <div
                              className="pointer-events-none absolute"
                              style={{
                                // 贴父容器右上边缘，不留缝隙
                                right: `${2 * scale}px`,
                                // 向下避开卡片上描边，避免首项上边框被角标遮盖
                                top: `${2 * scale}px`,
                              }}
                            >
                              <PriceBadge size={12} strong label={desc} badge={badge as BadgeType} scale={scale} />
                            </div>
                            <div
                              className="flex shrink-0 items-baseline justify-start whitespace-nowrap text-primary font-semibold proportional-nums"
                              style={{ minWidth: `${140 * scale}px` }}
                            >
                              <span
                                style={{
                                  fontSize: `${14 * scale}px`,
                                  lineHeight: `${20 * scale}px`,
                                  fontWeight: 600,
                                }}
                              >￥</span>
                              <span
                                className="font-semibold"
                                style={{
                                  fontSize: `${24 * scale}px`,
                                  lineHeight: `${32 * scale}px`,
                                  fontWeight: 600,
                                }}
                              >{price}</span>
                            </div>
                            <div
                              className="flex min-w-0 flex-1 items-baseline justify-end whitespace-nowrap font-semibold proportional-nums text-foreground"
                              style={{
                                gap: `${4 * scale}px`,
                                paddingRight: `${24 * scale}px`,
                              }}
                            >
                              <span
                                className="font-semibold"
                                style={{
                                  fontSize: `${24 * scale}px`,
                                  lineHeight: `${32 * scale}px`,
                                  fontWeight: 600,
                                }}
                              >{coins}</span>
                              <span
                                className="inline-flex items-baseline"
                                style={{
                                  fontSize: `${16 * scale}px`,
                                  lineHeight: `${24 * scale}px`,
                                  fontWeight: 600,
                                }}
                              >飞豆</span>
                              <span className="inline-flex items-baseline">
                                <TokenIcon size={15 * scale} />
                              </span>
                            </div>
                          </div>
                          <div
                            className="flex shrink-0 items-center justify-start"
                            style={{
                              minWidth: `${132 * scale}px`,
                              paddingLeft: `${18 * scale}px`,
                              paddingRight: 0,
                            }}
                          >
                            <div
                              className="flex items-baseline justify-start text-annotation-foreground font-semibold proportional-nums"
                              style={{
                                fontSize: `${16 * scale}px`,
                                lineHeight: `${24 * scale}px`,
                                fontWeight: 600,
                                marginTop: 0,
                                marginLeft: 0,
                                gap: `${4 * scale}px`,
                              }}
                            >
                              <span>{extraLabel ? extraLabel : `${extraCoins} 飞豆`}</span>
                            </div>
                            <div
                              className={cn(
                                "pointer-events-none absolute top-0 right-0 text-muted-foreground",
                                "bg-[linear-gradient(206.95deg,_rgba(229,232,248,1),_rgba(237,239,248,1)_100%)]",
                                "w-fit flex items-center justify-center rounded-bl-lg rounded-tr-lg"
                              )}
                              style={{
                                fontSize: `${12 * scale}px`,
                                lineHeight: `${16 * scale}px`,
                                paddingTop: `${4 * scale}px`,
                                paddingBottom: `${4 * scale}px`,
                                paddingLeft: `${12 * scale}px`,
                                paddingRight: `${12 * scale}px`,
                              }}
                            >
                              {memberForPricing ? "原价" : vip.vipDesc}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div
                className="flex min-h-0 min-w-0 flex-col overflow-x-hidden"
                style={{ flex: '539 1 0', minWidth: 0 }}
              >
                <PaymentBenefitsCompareTable
                  scale={scale}
                  variant="recharge"
                  isActive={isActive}
                  className="h-auto w-full max-w-full"
                />
              </div>
            </div>
          </div>
        </div>
        {leftColumnFooter ? (
          <div
            ref={rechargeFooterRef}
            className="absolute bottom-0 left-0 right-0 z-10 w-full h-fit bg-transparent pointer-events-none"
            style={{
              paddingLeft: `${32 * scale}px`,
              paddingRight: `${32 * scale}px`,
              paddingTop: `${12 * scale}px`,
              paddingBottom: 0,
            }}
          >
            {leftColumnFooter}
          </div>
        ) : null}
      </div>
    </div>
  );
}
